// CPU geometry: polygon fill triangulation (any winding rule, self-intersections)
// and polyline stroke expansion. Results are cached per node by the renderer.
import libtess from 'libtess';
import type { Vec2 } from '@vellum/core';

const tess = new libtess.GluTesselator();
let out: number[] = [];
tess.gluTessCallback(libtess.gluEnum.GLU_TESS_VERTEX_DATA, (data: number[], arr: number[]) => {
  arr.push(data[0], data[1]);
});
tess.gluTessCallback(libtess.gluEnum.GLU_TESS_BEGIN, () => {});
tess.gluTessCallback(libtess.gluEnum.GLU_TESS_ERROR, () => {});
tess.gluTessCallback(libtess.gluEnum.GLU_TESS_COMBINE, (coords: number[]) => [coords[0], coords[1], coords[2]]);
// Registering an edge-flag callback forces plain GL_TRIANGLES output.
tess.gluTessCallback(libtess.gluEnum.GLU_TESS_EDGE_FLAG, () => {});
tess.gluTessNormal(0, 0, 1);

/** Triangulate contours; returns flat [x0,y0,x1,y1,...] triangle list. */
export function fillTriangles(contours: Vec2[][], evenOdd: boolean): number[] {
  const res: number[] = [];
  // Contours whose boxes don't overlap can't change each other's fill (a point's winding only
  // counts contours around it), so each cluster is filled on its own: a lone convex contour as a
  // fan, the rest by libtess, which slows down sharply with size (dot patterns, outlined text).
  for (const group of overlapGroups(contours)) {
    if (group.length === 1 && isConvex(group[0])) {
      const c = group[0];
      for (let i = 1; i + 1 < c.length; i++) res.push(c[0].x, c[0].y, c[i].x, c[i].y, c[i + 1].x, c[i + 1].y);
    } else tessellate(group, evenOdd, res);
  }
  return res;
}

function tessellate(contours: Vec2[][], evenOdd: boolean, into: number[]) {
  out = into;
  tess.gluTessProperty(libtess.gluEnum.GLU_TESS_WINDING_RULE, evenOdd ? libtess.windingRule.GLU_TESS_WINDING_ODD : libtess.windingRule.GLU_TESS_WINDING_NONZERO);
  tess.gluTessBeginPolygon(out);
  for (const c of contours) {
    tess.gluTessBeginContour();
    for (const p of c) {
      const v = [p.x, p.y, 0];
      tess.gluTessVertex(v, v);
    }
    tess.gluTessEndContour();
  }
  tess.gluTessEndPolygon();
}

/** Contours (with 3+ points) in clusters of overlapping bounding boxes. */
function overlapGroups(contours: Vec2[][]): Vec2[][][] {
  const list = contours.filter((c) => c.length >= 3);
  if (list.length < 2) return list.length ? [list] : [];
  const boxes = list.map((c) => {
    let x0 = Infinity,
      y0 = Infinity,
      x1 = -Infinity,
      y1 = -Infinity;
    for (const p of c) {
      if (p.x < x0) x0 = p.x;
      if (p.x > x1) x1 = p.x;
      if (p.y < y0) y0 = p.y;
      if (p.y > y1) y1 = p.y;
    }
    return { x0, y0, x1, y1 };
  });
  const parent = list.map((_, i) => i);
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i])));
  // sweep along x: compare each box only with the ones still open
  const order = list.map((_, i) => i).sort((a, b) => boxes[a].x0 - boxes[b].x0);
  let open: number[] = [];
  for (const i of order) {
    const b = boxes[i];
    open = open.filter((j) => boxes[j].x1 >= b.x0);
    for (const j of open) if (boxes[j].y0 <= b.y1 && boxes[j].y1 >= b.y0) parent[find(j)] = find(i);
    open.push(i);
  }
  const groups = new Map<number, Vec2[][]>();
  // keep the original contour order inside each group
  list.forEach((c, i) => {
    const r = find(i);
    const g = groups.get(r);
    if (g) g.push(c);
    else groups.set(r, [c]);
  });
  return [...groups.values()];
}

function isConvex(pts: Vec2[]): boolean {
  const n = pts.length;
  if (n < 3) return false;
  let sign = 0;
  // turning the same way at every corner isn't enough: a star polygon does too, but goes round
  // more than once, so its edges flip direction (left/right, up/down) more than twice
  let flipsX = 0,
    flipsY = 0,
    lastX = 0,
    lastY = 0;
  for (let i = 0; i < n; i++) {
    const a = pts[i],
      b = pts[(i + 1) % n],
      c = pts[(i + 2) % n];
    const ux = b.x - a.x,
      uy = b.y - a.y;
    const cross = ux * (c.y - b.y) - uy * (c.x - b.x);
    if (Math.abs(cross) >= 1e-9) {
      const s = cross > 0 ? 1 : -1;
      if (sign === 0) sign = s;
      else if (s !== sign) return false;
    }
    const sx = ux > 1e-12 ? 1 : ux < -1e-12 ? -1 : 0,
      sy = uy > 1e-12 ? 1 : uy < -1e-12 ? -1 : 0;
    if (sx) {
      if (lastX && sx !== lastX) flipsX++;
      lastX = sx;
    }
    if (sy) {
      if (lastY && sy !== lastY) flipsY++;
      lastY = sy;
    }
  }
  return sign !== 0 && flipsX <= 2 && flipsY <= 2;
}

export type Join = 'MITER' | 'ROUND' | 'BEVEL';
export type Cap = 'NONE' | 'ROUND' | 'SQUARE';

/**
 * Expand a polyline into triangles. width in the same units as points.
 * `seg` controls round join/cap tessellation (segments per half circle).
 */
export function strokeTriangles(ptsIn: Vec2[], closed: boolean, width: number, join: Join, cap: Cap, outArr: number[], seg = 8, miterLimit = 4) {
  const pts = dedupe(ptsIn, closed);
  const n = pts.length;
  if (n < 2) return;
  const hw = width / 2;
  const tri = (ax: number, ay: number, bx: number, by: number, cx: number, cy: number) => outArr.push(ax, ay, bx, by, cx, cy);
  const segCount = closed ? n : n - 1;
  const normals: Vec2[] = [];
  for (let i = 0; i < segCount; i++) {
    const a = pts[i],
      b = pts[(i + 1) % n];
    const dx = b.x - a.x,
      dy = b.y - a.y;
    const l = Math.hypot(dx, dy) || 1;
    normals.push({ x: -dy / l, y: dx / l });
  }
  // segment quads
  for (let i = 0; i < segCount; i++) {
    const a = pts[i],
      b = pts[(i + 1) % n],
      nn = normals[i];
    const ax0 = a.x + nn.x * hw,
      ay0 = a.y + nn.y * hw,
      ax1 = a.x - nn.x * hw,
      ay1 = a.y - nn.y * hw;
    const bx0 = b.x + nn.x * hw,
      by0 = b.y + nn.y * hw,
      bx1 = b.x - nn.x * hw,
      by1 = b.y - nn.y * hw;
    tri(ax0, ay0, bx0, by0, bx1, by1);
    tri(ax0, ay0, bx1, by1, ax1, ay1);
  }
  // joins
  const joinAt = (i: number, n0: Vec2, n1: Vec2) => {
    const p = pts[i];
    const cross = n0.x * n1.y - n0.y * n1.x;
    if (Math.abs(cross) < 1e-6 && n0.x * n1.x + n0.y * n1.y > 0) return;
    // outer side
    const s = cross > 0 ? -1 : 1;
    const o0 = { x: p.x + n0.x * hw * s, y: p.y + n0.y * hw * s };
    const o1 = { x: p.x + n1.x * hw * s, y: p.y + n1.y * hw * s };
    if (join === 'ROUND') {
      const a0 = Math.atan2(o0.y - p.y, o0.x - p.x);
      let a1 = Math.atan2(o1.y - p.y, o1.x - p.x);
      let da = a1 - a0;
      while (da > Math.PI) da -= 2 * Math.PI;
      while (da < -Math.PI) da += 2 * Math.PI;
      const steps = Math.max(1, Math.ceil((Math.abs(da) / Math.PI) * seg));
      let prev = o0;
      for (let k = 1; k <= steps; k++) {
        const a = a0 + (da * k) / steps;
        const q = { x: p.x + Math.cos(a) * hw, y: p.y + Math.sin(a) * hw };
        tri(p.x, p.y, prev.x, prev.y, q.x, q.y);
        prev = q;
      }
      void a1;
      return;
    }
    tri(p.x, p.y, o0.x, o0.y, o1.x, o1.y);
    if (join === 'MITER') {
      const mx = n0.x + n1.x,
        my = n0.y + n1.y;
      const ml = Math.hypot(mx, my);
      if (ml < 1e-6) return;
      const cosHalf = ml / 2;
      const len = hw / cosHalf;
      if (len / hw > miterLimit) return;
      const m = { x: p.x + (mx / ml) * len * s, y: p.y + (my / ml) * len * s };
      tri(o0.x, o0.y, m.x, m.y, o1.x, o1.y);
    }
  };
  for (let i = 1; i < (closed ? n + 1 : n - 1); i++) {
    const i0 = (i - 1 + segCount) % segCount;
    const i1 = i % segCount;
    joinAt(i % n, normals[i0], normals[i1]);
  }
  if (!closed && cap !== 'NONE') {
    capAt(pts[0], normals[0], -1, hw, cap, tri, seg);
    capAt(pts[n - 1], normals[segCount - 1], 1, hw, cap, tri, seg);
  }
}

function capAt(p: Vec2, nn: Vec2, dir: number, hw: number, cap: Cap, tri: (...a: number[]) => void, seg: number) {
  const d = { x: nn.y * dir, y: -nn.x * dir }; // segment direction outward
  if (cap === 'SQUARE') {
    const a = { x: p.x + nn.x * hw, y: p.y + nn.y * hw },
      b = { x: p.x - nn.x * hw, y: p.y - nn.y * hw };
    const c = { x: b.x + d.x * hw, y: b.y + d.y * hw },
      e = { x: a.x + d.x * hw, y: a.y + d.y * hw };
    tri(a.x, a.y, b.x, b.y, c.x, c.y);
    tri(a.x, a.y, c.x, c.y, e.x, e.y);
  } else if (cap === 'ROUND') {
    const a0 = Math.atan2(nn.y, nn.x);
    const steps = seg;
    let prev = { x: p.x + nn.x * hw, y: p.y + nn.y * hw };
    for (let k = 1; k <= steps; k++) {
      const a = a0 + (k / steps) * Math.PI * (dir > 0 ? -1 : 1);
      const q = { x: p.x + Math.cos(a) * hw, y: p.y + Math.sin(a) * hw };
      tri(p.x, p.y, prev.x, prev.y, q.x, q.y);
      prev = q;
    }
  }
}

function dedupe(pts: Vec2[], closed: boolean): Vec2[] {
  const out: Vec2[] = [];
  for (const p of pts) {
    const l = out[out.length - 1];
    if (!l || Math.abs(l.x - p.x) > 1e-6 || Math.abs(l.y - p.y) > 1e-6) out.push(p);
  }
  if (closed && out.length > 1) {
    const a = out[0],
      b = out[out.length - 1];
    if (Math.abs(a.x - b.x) < 1e-6 && Math.abs(a.y - b.y) < 1e-6) out.pop();
  }
  return out;
}

/** Split a polyline into dash segments. */
export function dashPolyline(pts: Vec2[], closed: boolean, dash: number[]): Vec2[][] {
  if (!dash.length || dash.every((d) => d <= 0)) return [pts];
  const path = closed ? [...pts, pts[0]] : pts;
  const out: Vec2[][] = [];
  let di = 0;
  let left = dash[0];
  let on = true;
  let cur: Vec2[] = [path[0]];
  for (let i = 0; i + 1 < path.length; i++) {
    let a = path[i];
    const b = path[i + 1];
    let segLen = Math.hypot(b.x - a.x, b.y - a.y);
    while (segLen > 0) {
      const step = Math.min(left, segLen);
      const t = step / segLen;
      const p = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
      if (on) cur.push(p);
      segLen -= step;
      left -= step;
      a = p;
      if (left <= 1e-9) {
        if (on && cur.length > 1) out.push(cur);
        on = !on;
        di = (di + 1) % dash.length;
        left = dash[di] || 1;
        cur = [p];
      }
    }
  }
  if (on && cur.length > 1) out.push(cur);
  return out;
}
